import { IconWorld } from "central-icons/IconWorld";
import { useMemo, type FC } from "react";

import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { IconExternalLink } from "@/assets/icons/geist/IconExternalLink";
import type { BaseIconProps } from "@/assets/icons/utils";
import { withGitpodSize } from "@/assets/icons/utils";
import { SYSTEM_PORT_NAMES } from "@/components/command-palette/constants";
import { ButtonGroup, ButtonGroupItem } from "@/components/flexkit/ButtonGroup";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { Tooltip } from "@/components/Tooltip";
import type { PlainEnvironment } from "@/queries/environment-queries";

const IconWorldBase = withGitpodSize(IconWorld);

/** Force sm (16px) regardless of the size prop the parent passes. */
const IconWorldSized: FC<BaseIconProps> = (props) => <IconWorldBase {...props} size="sm" />;

type ActivePort = {
    name: string;
    port: number;
    url: string;
};

function portLabel(port: ActivePort): string {
    return port.name ? `${port.name} (:${port.port})` : `Port ${port.port}`;
}

export const SuperOnaOpenPortButton: FC<{ environment: PlainEnvironment }> = ({ environment }) => {
    const activePorts = useMemo(() => {
        const specPorts = environment.spec?.ports ?? [];
        const statusPorts = environment.status?.environmentUrls?.ports ?? [];

        const result: ActivePort[] = [];
        for (const spec of specPorts) {
            if (SYSTEM_PORT_NAMES.has(spec.name)) continue;
            const status = statusPorts.find((p) => p.port === spec.port);
            if (!status?.url) continue;
            result.push({ name: spec.name, port: spec.port, url: status.url });
        }
        return result;
    }, [environment.spec?.ports, environment.status?.environmentUrls?.ports]);

    if (activePorts.length === 0) {
        return null;
    }

    const primary = activePorts[0];

    // Single port: link styled as button
    if (activePorts.length === 1) {
        return (
            <Tooltip content={portLabel(primary)} usePortal>
                <ButtonGroup variant="secondary">
                    <ButtonGroupItem
                        asChild
                        LeadingIcon={IconWorldSized}
                        aria-label={`Open ${portLabel(primary)}`}
                        data-tracking-id="open-port-single-super-ona-header"
                    >
                        <a href={primary.url} target="_blank" rel="noopener noreferrer">
                            Preview
                        </a>
                    </ButtonGroupItem>
                </ButtonGroup>
            </Tooltip>
        );
    }

    // Multiple ports: split button with dropdown
    return (
        <ButtonGroup variant="secondary">
            <Tooltip content={portLabel(primary)} usePortal>
                <ButtonGroupItem
                    asChild
                    LeadingIcon={IconWorldSized}
                    aria-label={`Open ${portLabel(primary)}`}
                    data-tracking-id="open-port-primary-super-ona-header"
                >
                    <a href={primary.url} target="_blank" rel="noopener noreferrer">
                        Preview
                    </a>
                </ButtonGroupItem>
            </Tooltip>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <ButtonGroupItem
                        LeadingIcon={IconChevronDownSmall}
                        aria-label="Show all ports"
                        data-tracking-id="open-port-dropdown-trigger-super-ona-header"
                    />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-auto min-w-48">
                    {activePorts.map((port) => (
                        <DropdownMenuItem key={port.port} asChild>
                            <a
                                href={port.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-tracking-id="open-port-dropdown-super-ona-header"
                            >
                                <span className="mr-2">
                                    <IconExternalLink size="sm" />
                                </span>
                                {portLabel(port)}
                            </a>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </ButtonGroup>
    );
};
